import { useState, useEffect } from "react";
import { ObligacionAmbiental, useObligaciones } from "@/hooks/useObligaciones";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, Search, Filter, Download, Plus, Eye } from "lucide-react";
import NuevaObligacionModal from "./NuevaObligacionModal";
import DetalleObligacionModal from "./DetalleObligacionModal";
import { useModalPersistence } from "@/hooks/useModalPersistence";

interface ObligacionesTableProps {
  obligaciones: ObligacionAmbiental[];
  onRefresh: () => void;
  cardFilter?: 'all' | 'Cumplido' | 'En Proceso' | 'Pendiente' | 'Vencido' | 'ProximasVencer';
  onClearFilter?: () => void;
}

const ObligacionesTable = ({ obligaciones, onRefresh, cardFilter = 'all', onClearFilter }: ObligacionesTableProps) => {
  const [filteredObligaciones, setFilteredObligaciones] = useState(obligaciones);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterEstado, setFilterEstado] = useState("all");
  const [filterTipo, setFilterTipo] = useState("all");
  const [filterResponsable, setFilterResponsable] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const { modalState, openDetalleModal, closeDetalleModal } = useModalPersistence();
  const { updateObligacion } = useObligaciones();
  
  // Find the selected obligation based on persisted ID
  const selectedObligacion = modalState.selectedObligacionId 
    ? obligaciones.find(o => o.id === modalState.selectedObligacionId) || null
    : null;

  // Obtener valores únicos para filtros
  const responsables = [...new Set(obligaciones.map(o => o.responsable_interno))];

  // Aplicar filtros
  const applyFilters = () => {
    let filtered = obligaciones;

    if (searchTerm) {
      filtered = filtered.filter(o => 
        o.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        o.objetivo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        o.nombre_programa.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Aplicar filtro de tarjeta si está activo
    if (cardFilter === 'ProximasVencer') {
      // Filtrar próximas a vencer (próximos 30 días)
      const today = new Date();
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(today.getDate() + 30);
      
      filtered = filtered.filter(o => {
        if (o.estado === 'Cumplido' || o.estado === 'Vencido') return false;
        if (!o.vencimiento) return false;
        const vencimiento = new Date(o.vencimiento);
        return vencimiento >= today && vencimiento <= thirtyDaysFromNow;
      });
    } else if (cardFilter !== 'all') {
      filtered = filtered.filter(o => o.estado === cardFilter);
    } else if (filterEstado !== "all") {
      // Solo aplicar filtro de select si no hay filtro de tarjeta
      filtered = filtered.filter(o => o.estado === filterEstado);
    }

    if (filterTipo !== "all") {
      filtered = filtered.filter(o => o.tipo === filterTipo);
    }

    if (filterResponsable !== "all") {
      filtered = filtered.filter(o => o.responsable_interno === filterResponsable);
    }

    setFilteredObligaciones(filtered);
  };

  // Aplicar filtros cuando cambien los valores
  useEffect(() => {
    applyFilters();
  }, [searchTerm, filterEstado, filterTipo, filterResponsable, cardFilter]);

  // Actualizar cuando cambien las obligaciones
  useEffect(() => {
    setFilteredObligaciones(obligaciones);
    applyFilters();
  }, [obligaciones]);

  const handleNuevaObligacion = () => {
    // Refresh the parent's data after successful creation
    onRefresh();
    setIsModalOpen(false);
  };

  const handleVerDetalle = (obligacion: ObligacionAmbiental) => {
    openDetalleModal(obligacion.id);
  };

  const handleUpdateObligacion = async (obligacionActualizada: ObligacionAmbiental) => {
    try {
      // Guardar los cambios en la base de datos
      await updateObligacion(obligacionActualizada.id, {
        codigo: obligacionActualizada.codigo,
        nombre_programa: obligacionActualizada.nombre_programa,
        tipo: obligacionActualizada.tipo,
        objetivo: obligacionActualizada.objetivo,
        frecuencia: obligacionActualizada.frecuencia,
        entidad_reporte: obligacionActualizada.entidad_reporte,
        responsable_interno: obligacionActualizada.responsable_interno,
        estado: obligacionActualizada.estado,
        vencimiento: obligacionActualizada.vencimiento,
        indicadores: obligacionActualizada.indicadores,
        nivel_cumplimiento: obligacionActualizada.nivel_cumplimiento,
        plazo_cumplimiento: obligacionActualizada.plazo_cumplimiento,
        observaciones: obligacionActualizada.observaciones,
        evidencia: obligacionActualizada.evidencia,
      });
      
      // Refresh the parent's data after successful update
      onRefresh();
    } catch (error) {
      console.error('Error al actualizar obligación:', error);
    }
  };

  const getEstadoBadge = (estado: string) => {
    const variants = {
      'Cumplido': 'default',
      'En Proceso': 'secondary',
      'Pendiente': 'outline',
      'Vencido': 'destructive'
    } as const;

    return (
      <Badge variant={variants[estado as keyof typeof variants] || 'outline'}>
        {estado}
      </Badge>
    );
  };

  const getTipoBadge = (tipo: string) => {
    const colors = {
      'Ambiental': 'bg-environmental/10 text-environmental border-environmental/20',
      'Social': 'bg-blue-100 text-blue-700 border-blue-200',
      'Biótico': 'bg-green-100 text-green-700 border-green-200'
    };

    return (
      <Badge className={colors[tipo as keyof typeof colors] || ''}>
        {tipo}
      </Badge>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Matriz de Obligaciones Ambientales
          </CardTitle>
          <div className="flex gap-2">
            <Button 
              onClick={() => setIsModalOpen(true)}
              className="bg-viable-orange hover:bg-viable-orange-dark text-white"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva Obligación
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Indicador de filtro activo desde tarjetas */}
        {cardFilter !== 'all' && (
          <div className="mb-4 p-3 bg-primary/10 border border-primary/20 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">
                Filtrando por: <strong>{cardFilter === 'ProximasVencer' ? 'Próximas a Vencer' : cardFilter}</strong>
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilter}
              className="h-auto py-1 px-2"
            >
              Limpiar filtro
            </Button>
          </div>
        )}

        {/* Filtros Simples */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por código o programa..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select 
            value={cardFilter !== 'all' ? cardFilter : filterEstado} 
            onValueChange={setFilterEstado}
            disabled={cardFilter !== 'all'}
          >
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Filtrar por estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="Pendiente">⚠️ Pendiente</SelectItem>
              <SelectItem value="En Proceso">🔄 En Proceso</SelectItem>
              <SelectItem value="Vencido">❌ Vencido</SelectItem>
              <SelectItem value="Cumplido">✅ Cumplido</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tabla */}
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código</TableHead>
                <TableHead>Nombre del Programa</TableHead>
                <TableHead>Responsable</TableHead>
                <TableHead>Vence</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredObligaciones.map((obligacion) => (
                <TableRow 
                  key={obligacion.id} 
                  className="hover:bg-muted/50 cursor-pointer"
                  onClick={() => handleVerDetalle(obligacion)}
                >
                  <TableCell className="font-medium text-sm">
                    {obligacion.codigo}
                  </TableCell>
                  <TableCell className="max-w-md">
                    <div className="space-y-1">
                      <div className="font-medium text-sm" title={obligacion.nombre_programa}>
                        {obligacion.nombre_programa}
                      </div>
                      <div className="flex gap-2">
                        {getTipoBadge(obligacion.tipo)}
                        <span className="text-xs text-muted-foreground">{obligacion.frecuencia}</span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{obligacion.responsable_interno}</TableCell>
                  <TableCell className="text-sm">{obligacion.vencimiento}</TableCell>
                  <TableCell>
                    {getEstadoBadge(obligacion.estado)}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="h-8 w-8 p-0"
                      onClick={() => handleVerDetalle(obligacion)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {filteredObligaciones.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            No se encontraron obligaciones con los filtros aplicados.
          </div>
        )}
      </CardContent>
      
      <NuevaObligacionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleNuevaObligacion}
      />
      
        <DetalleObligacionModal
          obligacion={selectedObligacion}
          isOpen={modalState.isDetalleModalOpen}
          onClose={closeDetalleModal}
          onUpdate={handleUpdateObligacion}
          onRefresh={onRefresh}
          activeTab={modalState.activeTab}
        />
    </Card>
  );
};

export default ObligacionesTable;